import apiService from '../api'

export interface ShipmentApi {
  id: string
  booking?: string | null
  billOfLading?: string | null
  bl?: string | null
  containerNumber?: string | null
  carrier?: string | null
  vessel?: string | null
  pol?: string | null
  pod?: string | null
  eta?: string | null
  etd?: string | null
  status?: string | null
  product?: string | null
  customer?: string | null
  buyContract?: string | null
  saleContract?: string | null
  factory?: string | null
  shipmentContainers?: { containerNumber?: string | null }[] | null
  [key: string]: unknown
}

export interface ShipmentDisplay {
  id: string
  buyContract?: string
  saleContract?: string
  booking: string
  billOfLading: string
  containerNumber: string
  containerNumbers: string[]
  vessel: string
  shippingLine: string
  product: string
  customer: string
  factory: string
  origin: string
  destination: string
  etd: string
  eta: string
  /** ISO date (YYYY-MM-DD) for form inputs */
  etdInput: string
  /** ISO date (YYYY-MM-DD) for form inputs */
  etaInput: string
  status: string
}

export interface ShipmentsListResponse {
  shipments: ShipmentApi[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

/** Canonical status → display label (used across Shipments, Dashboard, Clearing). */
export const SHIPMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_coordination: 'On Coordination',
  in_transit: 'In Transit',
  arrived: 'Arrived at Port',
  delayed: 'Delayed',
  cleared: 'Cleared',
  unpacked: 'Unpacked',
  in_storage: 'In Storage',
  partially_delivered: 'Partially Delivered',
  fully_delivered: 'Fully Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/** Values must match backend carrier adapters (see getCarrierAdapter). */
export const INTEGRATED_SHIPPING_LINE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Hapag-Lloyd', label: 'Hapag-Lloyd' },
  { value: 'Maersk', label: 'Maersk' },
  { value: 'MSC', label: 'MSC' },
  { value: 'DSV', label: 'DSV' },
]

export const SHIPMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_coordination', label: 'On Coordination' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'arrived', label: 'Arrived at Port' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'unpacked', label: 'Unpacked' },
  { value: 'in_storage', label: 'In Storage' },
  { value: 'partially_delivered', label: 'Partially Delivered' },
  { value: 'fully_delivered', label: 'Fully Delivered' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

export function getStatusBadgeFromBackend(status: string | null | undefined): { status: 'in_transit' | 'delayed' | 'arrived' | 'pending' | 'completed'; label: string } {
  const s = (status ?? '').toLowerCase().trim()
  if (s === 'transport' || s === 'equipment' || s === 'shipment' || s === 'unknown') {
    return { status: 'in_transit', label: 'In Transit' }
  }
  const label = SHIPMENT_STATUS_LABELS[s] ?? (status || 'Pending')
  if (s === 'in_transit') return { status: 'in_transit', label }
  if (s === 'arrived') return { status: 'arrived', label }
  if (s === 'delayed') return { status: 'delayed', label }
  if (['cleared', 'unpacked', 'in_storage', 'fully_delivered', 'partially_delivered', 'completed'].includes(s)) return { status: 'completed', label }
  if (s === 'cancelled') return { status: 'pending', label: 'Cancelled' }
  return { status: 'pending', label }
}

/** Format ISO date string for date inputs (YYYY-MM-DD). */
export function formatDateForInput(v: string | null | undefined): string {
  if (v == null || String(v).trim() === '') return ''
  try {
    const d = new Date(String(v).trim())
    if (Number.isNaN(d.getTime())) return ''
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  } catch {
    return ''
  }
}

/** Format ISO date string for list display (e.g. "5 Jan 2026"). Returns "—" if empty/invalid. */
function formatDateForList(v: string | null | undefined): string {
  if (v == null || String(v).trim() === '') return '—'
  try {
    const d = new Date(String(v).trim())
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return '—'
  }
}

export function listShipmentContainers(s: {
  containerNumber?: string | null
  shipmentContainers?: { containerNumber?: string | null }[] | null
}): string[] {
  const nested = (s.shipmentContainers ?? [])
    .map((row) => (row.containerNumber ?? '').trim())
    .filter(Boolean)
  const primary = (s.containerNumber ?? '').trim()
  return [...new Set([primary, ...nested].filter(Boolean))]
}

export function formatShipmentContainers(s: {
  containerNumber?: string | null
  shipmentContainers?: { containerNumber?: string | null }[] | null
}): string {
  const unique = listShipmentContainers(s)
  if (unique.length === 0) return '—'
  if (unique.length === 1) return unique[0]
  return `${unique.length} containers`
}

export function shipmentToDisplay(s: ShipmentApi): ShipmentDisplay {
  const fmt = (v: string | null | undefined) => (v != null && String(v).trim() !== '' ? String(v).trim() : '—')
  return {
    id: s.id,
    buyContract: fmt(s.buyContract) !== '—' ? fmt(s.buyContract) : undefined,
    saleContract: fmt(s.saleContract) !== '—' ? fmt(s.saleContract) : undefined,
    booking: fmt(s.booking),
    billOfLading: fmt(s.billOfLading ?? s.bl),
    containerNumber: formatShipmentContainers(s),
    containerNumbers: listShipmentContainers(s),
    vessel: fmt(s.vessel),
    shippingLine: fmt(s.carrier),
    product: fmt(s.product),
    customer: fmt(s.customer),
    factory: fmt(s.factory),
    origin: fmt(s.pol),
    destination: fmt(s.pod),
    etd: formatDateForList(s.etd),
    eta: formatDateForList(s.eta),
    etdInput: formatDateForInput(s.etd),
    etaInput: formatDateForInput(s.eta),
    status: (s.status != null && String(s.status).trim() !== '' ? String(s.status).trim() : 'pending'),
  }
}

export async function getShipmentCustomers(): Promise<string[]> {
  const res = await apiService.get<{
    success?: boolean
    data?: { customers?: string[] }
    customers?: string[]
  }>('/shipments/customers')
  const customers = res?.data?.customers ?? res?.customers
  return Array.isArray(customers) ? customers.filter((c) => typeof c === 'string' && c.trim()) : []
}

/** Fallback when /shipments/customers is unavailable — scan shipment rows for names. */
export async function loadShipmentCustomerOptions(): Promise<string[]> {
  try {
    const fromApi = await getShipmentCustomers()
    if (fromApi.length > 0) return fromApi
  } catch {
    // Older backend without /customers — fall through
  }

  const { shipments } = await getShipments({ page: 1, limit: 500 })
  const names = [
    ...new Set(
      shipments
        .map((s) => (s.customer != null ? String(s.customer).trim() : ''))
        .filter((c) => c.length > 0)
    ),
  ]
  names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  return names
}

export async function getShipments(params?: {
  page?: number
  limit?: number
  search?: string
  status?: string
  shippingLine?: string
  customer?: string
}): Promise<ShipmentsListResponse> {
  const res = await apiService.get<{ success: boolean; data: { shipments: ShipmentApi[]; meta: ShipmentsListResponse['meta'] } }>('/shipments', { params })
  const data = (res as any)?.data ?? res
  return {
    shipments: data.shipments ?? [],
    meta: data.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

/** Get a single shipment by id (for opening details when navigating from e.g. AI Actions). */
export async function getShipment(id: string): Promise<ShipmentDisplay> {
  const res = await apiService.get<{ success: boolean; data: ShipmentApi }>(`/shipments/${id}`)
  const data = (res as any)?.data ?? res
  return shipmentToDisplay(data as ShipmentApi)
}

export async function deleteShipment(id: string): Promise<void> {
  await apiService.delete(`/shipments/${id}`)
}

/** Trigger carrier sync for a shipment (updates status, ETA, ETD, events from carrier API). */
export async function syncShipmentTracking(shipmentId: string): Promise<{ updated: boolean; eventsStored: number; status?: string; eta?: string | null; etd?: string | null; error?: string }> {
  const res = await apiService.post<{ success: boolean; data: { syncResult: { updated: boolean; eventsStored: number; status?: string; eta?: string | null; etd?: string | null; error?: string } } }>(
    `/shipments/${shipmentId}/sync-tracking`
  )
  const data = (res as any)?.data ?? res
  const syncResult = data?.syncResult ?? {}
  return syncResult
}

/** Bulk carrier sync — refreshes oldest-updated shipments first (status, ETA, ETD from carrier). */
export async function syncAllShipmentTracking(options?: {
  carrier?: string
  limit?: number
}): Promise<{ processed: number; updated: number; errors: number; skipped: number }> {
  const res = await apiService.post<{
    success: boolean
    data: { processed: number; updated: number; errors: number; skipped: number }
  }>('/shipments/sync-tracking/bulk', {}, { params: options })
  const data = (res as any)?.data ?? res
  return data
}

/** Delete all shipments (and their clearing records via cascade). Returns number deleted. */
export async function deleteAllShipments(): Promise<{ deleted: number }> {
  const res = await apiService.delete<{ success: boolean; data: { deleted: number } }>('/shipments/all')
  const data = (res as any)?.data ?? res
  return { deleted: data?.deleted ?? 0 }
}

export interface ShipmentCreatePayload {
  buyContract?: string
  saleContract?: string
  buyContractId?: string
  saleContractId?: string
  booking?: string
  containerNumber?: string
  carrier?: string
  pol?: string
  pod?: string
  eta?: string
  etd?: string
  bl?: string
  vessel?: string
  product?: string
  customer?: string
  factory?: string
  status?: string
}

export async function createShipment(payload: ShipmentCreatePayload): Promise<ShipmentApi> {
  const res = await apiService.post<{ success: boolean; data: ShipmentApi }>('/shipments', payload)
  const data = (res as any)?.data ?? res
  return data
}

export interface ShipmentBulkCreatePayload extends Omit<ShipmentCreatePayload, 'containerNumber'> {
  containerNumbers: string[]
}

export interface ShipmentBulkCreateResponse {
  shipments: ShipmentApi[]
  count: number
  booking: string
}

export async function createShipmentsBulk(
  payload: ShipmentBulkCreatePayload
): Promise<ShipmentBulkCreateResponse> {
  const res = await apiService.post<{ success: boolean; data: ShipmentBulkCreateResponse }>(
    '/shipments/bulk',
    payload
  )
  const data = (res as any)?.data ?? res
  return {
    shipments: data.shipments ?? [],
    count: data.count ?? data.shipments?.length ?? 0,
    booking: data.booking ?? payload.booking ?? '',
  }
}

export interface ShipmentUpdatePayload {
  buyContract?: string
  saleContract?: string
  booking?: string
  bl?: string
  containerNumber?: string
  vessel?: string
  carrier?: string
  product?: string
  customer?: string
  factory?: string
  pol?: string
  pod?: string
  etd?: string
  eta?: string
  status?: string
}

export async function updateShipment(id: string, payload: ShipmentUpdatePayload): Promise<ShipmentApi> {
  const res = await apiService.put<{ success: boolean; data: ShipmentApi }>(`/shipments/${id}`, payload)
  const data = (res as any)?.data ?? res
  return data
}

export async function populateFromContracts(params: {
  buyContractId?: string
  saleContractId?: string
}): Promise<Record<string, string | null>> {
  const searchParams = new URLSearchParams()
  if (params.buyContractId) searchParams.set('buyContractId', params.buyContractId)
  if (params.saleContractId) searchParams.set('saleContractId', params.saleContractId)
  const res = await apiService.get<{ success: boolean; data: Record<string, string | null> }>(
    `/shipments/populate-from-contracts?${searchParams.toString()}`
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function populateForOrder(shipmentId: string): Promise<Record<string, any>> {
  const res = await apiService.get<{ success: boolean; data: Record<string, any> }>(
    `/shipments/populate-for-order?shipmentId=${shipmentId}`
  )
  const data = (res as any)?.data ?? res
  return data
}

/** Live tracking from carrier API. May fail if carrier not supported or no BL. */
export interface ShipmentTrackResponse {
  status?: string
  eta?: string
  etd?: string
  lastEvent?: { type?: string; location?: string; dateTime?: string }
  events?: Array<{ type?: string; eventType?: string; location?: string; dateTime?: string }>
}

export async function getShipmentTrack(
  shipmentId: string,
  by: 'bl' | 'container' | 'booking' = 'bl'
): Promise<ShipmentTrackResponse> {
  const res = await apiService.get<{ success: boolean; data: ShipmentTrackResponse }>(
    `/shipments/${shipmentId}/track`,
    { params: { by } }
  )
  const data = (res as any)?.data ?? res
  return data
}

/** Stored carrier tracking events (from sync). */
export interface TrackingEvent {
  eventType?: string
  description?: string
  location?: string
  eventDateTime?: string
}

export interface ShipmentTrackingEventsResponse {
  shipmentId: string
  carrier: string | null
  events: TrackingEvent[]
}

export async function getShipmentTrackingEvents(
  shipmentId: string
): Promise<ShipmentTrackingEventsResponse> {
  const res = await apiService.get<{ success: boolean; data: ShipmentTrackingEventsResponse }>(
    `/shipments/${shipmentId}/tracking-events`
  )
  const data = (res as any)?.data ?? res
  return data
}

